import React, { useRef, useEffect } from 'react';
import { ChatMessage } from '../ChatMessage';
import { ChatInput } from '../ChatInput';
import { Loader } from '../Loader';
import { Message, Email } from '../../types/api';
import { EmailThreadView } from './EmailThreadView';

type ThreadType = 'whatsapp' | 'email';

interface ThreadContentProps {
  threadType?: ThreadType;
  messages?: Message[];
  emails?: Email[];
  isLoading?: boolean;
  error?: string | null;
  isMasterDetail?: boolean;
  // WhatsApp input props
  messageInput?: string;
  setMessageInput?: (v: string) => void;
  isAiMode?: boolean;
  isSendingMessage?: boolean;
  onSendMessage?: () => void;
  onModeChange?: (ai: boolean) => void;
  onManualModeClick?: () => void;
  showTooltip?: boolean;
  tooltipMessage?: string;
}

export const ThreadContent: React.FC<ThreadContentProps> = ({
  threadType = 'whatsapp',
  messages = [],
  emails = [],
  isLoading = false,
  error = null,
  isMasterDetail = false,
  messageInput = '',
  setMessageInput,
  isAiMode = true,
  isSendingMessage = false,
  onSendMessage,
  onModeChange,
  onManualModeClick,
  showTooltip = false,
  tooltipMessage = ''
}) => {
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    if (threadType === 'whatsapp' && messages.length > 0) {
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    } else if (threadType === 'email' && emails.length > 0) {
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    }
  }, [messages, emails, threadType]);

  return (
    <>
      {/* Email Thread View */}
      {threadType === 'email' ? (
        <EmailThreadView
          emails={emails}
          isLoading={isLoading}
          error={error}
          isMasterDetail={isMasterDetail}
        />
      ) : (
        <>
          {/* WhatsApp Chat Messages */}
          <div className="flex-1 flex flex-col overflow-hidden min-h-0">
            {isLoading ? (
              <div className="flex items-center justify-center h-full">
                <Loader />
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center py-16 px-4">
                <p className="font-sans font-medium text-base text-text-subtle text-center">
                  {error}
                </p>
              </div>
            ) : messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 px-4">
                <p className="font-sans font-medium text-base text-text-subtle text-center">
                  No hay mensajes en esta conversación
                </p>
              </div>
            ) : (
              <div
                ref={messagesContainerRef}
                className={`overflow-y-auto flex-1 flex flex-col ${
                  isMasterDetail ? 'pb-4' : 'pb-36'
                }`}
              >
                {/* Messages container with max-width for readability */}
                <div className="flex flex-col px-4 py-4 gap-1 max-w-2xl mx-auto w-full">
                  {messages.map((message) => {
                    const isFromCustomer = message.isFromCustomer;
                    let timestamp = '';
                    if (message.timestamp) {
                      const date = new Date(message.timestamp);
                      if (!isNaN(date.getTime())) {
                        timestamp = date.toLocaleTimeString('es-CL', {
                          hour: '2-digit',
                          minute: '2-digit'
                        });
                      }
                    }

                    const isManual = message.senderType === 'store_admin';

                    return (
                      <ChatMessage
                        key={message.id}
                        text={message.body}
                        timestamp={timestamp}
                        isFromUser={!isFromCustomer}
                        showAuthor={false}
                        isManual={isManual}
                      />
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Chat Input - Fixed at bottom (or relative in master-detail) */}
          {setMessageInput && onSendMessage && onModeChange && (
            <div
              className={`${
                isMasterDetail
                  ? 'relative py-3 bg-muted '
                  : 'fixed bottom-0 left-0 right-0 w-full z-50 py-3 bg-muted'
              }`}
            >
              <div className={`${isMasterDetail ? 'px-3' : 'max-w-[1280px] mx-auto px-3'}`}>
                <div className="flex gap-3 items-end w-full max-w-[600px] mx-auto">
                  <ChatInput
                    messageInput={messageInput}
                    setMessageInput={setMessageInput}
                    isAiMode={isAiMode}
                    isSendingMessage={isSendingMessage}
                    onSendMessage={onSendMessage}
                    onModeChange={onModeChange}
                    onManualModeClick={onManualModeClick}
                    showTooltip={showTooltip}
                    tooltipMessage={tooltipMessage}
                  />
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default ThreadContent;
